import {
  takeLatest, put, select, all, call,
} from 'redux-saga/effects';
import { message } from 'antd';
import { get } from 'lodash';
import { fetchDashboard } from '@api/dashboards';
import { defaultMetricColumns, transformOldMetrics } from '@components/viewer-sessions/utils';
import { autoRequest } from '@api/createRequestStore';
import * as baseActions from '@store/errorDiagnostics/base/actions';
import * as birdChartActions from '@store/errorDiagnostics/birdChart/actions';
import * as dimensionTablesActions from '@store/errorDiagnostics/dimensionTables/actions';
import { updateSelectedMetrics } from '@store/viewerSessions/actions';
import scrollingWindowFromTo from '@utils/scrollingWindowFromTo';
import { featureSelector } from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';

import * as baseTypes from '../base/types';
import * as birdChartTypes from '../birdChart/types';
import * as errorTableTypes from '../errorTable/types';
import * as dimensionTablesTypes from '../dimensionTables/types';
import * as barChartTypes from '../barChart/types';
import * as actions from './actions';
import * as types from './types';
import { dashboardResponseSelector } from './selectors';

function* init() {
  yield all([
    put({ type: baseTypes.INIT }),
    put({ type: birdChartTypes.INIT }),
    put({ type: errorTableTypes.INIT }),
    put({ type: dimensionTablesTypes.INIT }),
    put({ type: barChartTypes.INIT }),
  ]);
}

function* afterRequest() {
  const dashboard = yield select(dashboardResponseSelector);
  const timelineEnabled = yield select(featureSelector(featureNames.VIEWER_DRILLDOWN_TIMELINE));
  if (!dashboard) {
    return;
  }
  const { config } = dashboard;
  try {
    const {
      base, dimensionTables, birdChart, viewerSessionsTable,
    } = config;
    const { metric, currentErrorCode } = base;
    let selectedMetrics = get(viewerSessionsTable, ['selectedMetrics'], []);
    const customerAccountType = yield select(featureSelector(featureNames.CUSTOMER_ACCOUNT_TYPE));
    const defaultColumns = defaultMetricColumns(metric, timelineEnabled, customerAccountType);
    if (!selectedMetrics.length) {
      selectedMetrics = defaultColumns;
    }
    const res = scrollingWindowFromTo(config);
    yield call(init);
    yield all([
      put(
        baseActions.setBaseState(res),
      ),
      put(birdChartActions.setBirdChartState(birdChart)),
      put(dimensionTablesActions.setDimensionTablesState(dimensionTables)),
      put(updateSelectedMetrics(transformOldMetrics(selectedMetrics), currentErrorCode)),
    ]);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function* initRequest() {
  yield put(actions.initRequest());
}

function* fetching({ id }) {
  yield actions.fetching();
  try {
    yield autoRequest(fetchDashboard, types.SCOPE, id, afterRequest);
  } catch (error) {
    yield call(message.error, error.message || 'Unknown error.');
    throw error;
  }
}

function* watchInitRequest() {
  yield takeLatest([types.INIT], initRequest);
}

function* watchFetching() {
  yield takeLatest([types.GET_DASHBOARD], fetching);
}

export default [watchInitRequest, watchFetching];
